"use client";

import { BannerPage } from "@/components/formats/banner-page";
import { PostPage } from "@/components/formats/post-page";
import {
  Banner,
  RouteWrapper,
  TitleShadow,
  CategoryGrid,
  CategoryContainer,
  NavLink,
} from "@/components/styles";
import Footer from "@/components/ui/footer";
import Image from "@/components/ui/image";
import { useBackHandler } from "@/lib/hooks/useBackHanlder";
import useUserStore from "@/state/user/useUserStore";
import { Asset } from "@/types/types";
import { ValidatorWidget } from "@0xhoneyjar/validator-widget";
import { useMediaQuery } from "@mantine/hooks";
import { trackEvent } from "@openpanel/nextjs";
import { Disc3, Eye, GraduationCap, Shirt, Smile } from "lucide-react";
import { useEffect } from "react";
import { ScrollView, Window, WindowContent, WindowHeader } from "react95";
import Link from "next/link";
import { m } from "motion/react";
import { usePathname } from "next/navigation";
import Navbar from "@/components/ui/navbar";
import MobileTemplatePage from "@/components/ui/mobile-template-page";

export default function Page() {
  const pathname = usePathname();
  const isSmallScreen = useMediaQuery("(max-width: 768px)");

  const [imageFormat, setRestrictedFonts, assetsCount] = useUserStore(
    (state) => [state.imageFormat, state.setRestrictedFonts, state.assetsCount]
  );

  const HeadAssets = Array.from(
    { length: assetsCount?.base },
    (_, i) => `/Mememaker/base/base${i + 1}.png`
  );

  const ClothingAssets = Array.from(
    { length: assetsCount?.clothing },
    (_, i) => `/Mememaker/clothing/clothing${i + 1}.png`
  );

  const FaceAssets = Array.from(
    { length: assetsCount?.headgear },
    (_, i) => `/Mememaker/headgear/headgear${i + 1}.png`
  );

  const EyeAssets = Array.from(
    { length: assetsCount?.face },
    (_, i) => `/Mememaker/face/face${i + 1}.png`
  );

  const MiscAssets = Array.from(
    { length: assetsCount?.misc },
    (_, i) => `/Mememaker/misc/misc${i + 1}.png`
  ).reverse();

  const allAssets: Asset[] = [
    { assets: HeadAssets, icon: <Smile /> },
    { assets: ClothingAssets, icon: <Shirt /> },
    { assets: FaceAssets, icon: <GraduationCap /> },
    { assets: EyeAssets, icon: <Eye /> },
    { assets: MiscAssets, icon: <Disc3 /> },
  ];

  const backHandler = useBackHandler();

  useEffect(() => {
    backHandler();
    setRestrictedFonts([
      { value: "title1", label: "Honey" },
      { value: "title2", label: "Comic" },
      { value: "title3", label: "Pixel" },
      { value: "title9", label: "Impact" },
      { value: "title10", label: "Lowpoly" },
    ]);
  }, []);

  const templateCategories = [
    {
      title: "henlo",
      type: "henlo",
      path: "/henlo",
      background: "/Mememaker/henlo3/background/bg2.png",
    },
    {
      title: "the honey jar",
      type: "thj",
      path: "/thj",
      background: "/Mememaker/folders/thj.jpeg",
    },
  ];

  return (
    <>
      {isSmallScreen === true && <MobileTemplatePage />}
      {isSmallScreen === false && (
        <RouteWrapper>
          <Window
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              fontFamily: "var(--font-cabin-condensed)",
              overflow: "hidden",
            }}
          >
            <strong>
              <WindowHeader
                className="window-title"
                style={{ background: "#623a00" }}
              >
                <div
                  style={{
                    gap: 8,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      position: "relative",
                    }}
                  >
                    <Image
                      src={"/Mememaker/beraflip/beraflip.png"}
                      alt={"beraflip"}
                      fill
                      style={{
                        objectFit: "contain",
                      }}
                    />
                  </div>
                  <span style={{ fontWeight: 500 }}>Beraflip</span>
                </div>
              </WindowHeader>
            </strong>
            <WindowContent
              style={{
                padding: 8,
                boxSizing: "border-box",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ScrollView
                style={{
                  width: "100%",
                  flexGrow: "1",
                  background: "#ddd",
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  maxHeight: "calc(100vh - 62px)",
                }}
              >
                <Navbar />
                {(() => {
                  switch (imageFormat) {
                    case "banner":
                      return (
                        <BannerPage
                          color={"#eeba02"}
                          assets={allAssets}
                          websiteLink="https://www.0xhoneyjar.xyz/"
                        />
                      );
                    case "post":
                      return (
                        <PostPage
                          color={"#eeba02"}
                          assets={allAssets}
                          websiteLink="https://www.0xhoneyjar.xyz/"
                        />
                      );
                    default:
                      return (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "space-between",
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              height: "auto",
                              display: "flex",
                              flexDirection: "column",
                              boxSizing: "border-box",
                              padding: "18px 36px 36px 36px",
                              gap: "12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                marginBottom: "16px",
                              }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "start",
                                  justifyContent: "center",
                                  flexDirection: "column",
                                }}
                              >
                                <TitleShadow
                                  style={{ margin: 0, marginBottom: "4px" }}
                                >
                                  Templates
                                </TitleShadow>
                                <p
                                  style={{
                                    margin: 0,
                                    fontFamily: "var(--font-cabin-condensed)",
                                  }}
                                >
                                  Select a meme template or upload your own to
                                  start creating your own meme.
                                </p>
                              </div>
                            </div>
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                flexDirection: "row",
                                gap: "16px",
                              }}
                            >
                              <CategoryGrid>
                                {templateCategories.map((category) => (
                                  <Link
                                    href={category.path ?? ""}
                                    key={category.type}
                                    style={{ textDecoration: "none" }}
                                  >
                                    <CategoryContainer>
                                      {category.background && (
                                        <m.div
                                          whileHover={{
                                            scale: 1.05,
                                            transition: {
                                              duration: 0.15,
                                              ease: "easeOut",
                                            },
                                          }}
                                          style={{
                                            position: "absolute",
                                            width: "100%",
                                            height: "100%",
                                            left: 0,
                                            top: 0,
                                          }}
                                        >
                                          <Image
                                            src={category.background}
                                            alt={"background"}
                                            fill
                                            style={{
                                              objectFit: "cover",
                                            }}
                                          />
                                          <div
                                            style={{
                                              position: "absolute",
                                              bottom: 0,
                                              left: 0,
                                              width: "100%",
                                              height: "25%",
                                              background:
                                                "linear-gradient(to bottom, transparent, rgba(0,0,0,0.75))",
                                            }}
                                          />
                                        </m.div>
                                      )}
                                      <div
                                        style={{
                                          fontFamily:
                                            "var(--font-cabin-condensed)",
                                          fontSize: "1.2rem",
                                          color: "#000",
                                          zIndex: 100,
                                          position: "absolute",
                                          bottom: 16,
                                          left: 16,
                                          textAlign: "center",
                                        }}
                                      >
                                        <TitleShadow
                                          style={{
                                            padding: 0,
                                            margin: 0,
                                            fontSize: "1.6rem",
                                          }}
                                        >
                                          {category.title}
                                        </TitleShadow>
                                      </div>
                                    </CategoryContainer>
                                  </Link>
                                ))}
                              </CategoryGrid>
                            </div>
                          </div>
                          <Footer />
                        </div>
                      );
                  }
                })()}
              </ScrollView>
            </WindowContent>
          </Window>
        </RouteWrapper>
      )}
    </>
  );
}
